export const ASSESSMENT_OUTCOME_SCHEMA_VERSION = 1;
export const MAX_CUSTOM_OUTCOMES = 15;

export const GRADING_TYPES = Object.freeze([
  "scoreOnly",
  "passFail",
  "gcse9to1",
  "dmpf",
  "custom",
]);

export const REATTEMPT_MODES = Object.freeze([
  "disabled",
  "always",
  "belowPercentage",
  "belowGrade",
]);

export const FINAL_RESULT_POLICIES = Object.freeze([
  "highest",
  "latest",
  "average",
  "first",
]);

export const DEFAULT_ASSESSMENT_OUTCOME_CONFIG = Object.freeze({
  schemaVersion: ASSESSMENT_OUTCOME_SCHEMA_VERSION,
  grading: Object.freeze({
    type: "scoreOnly",
    schemeName: null,
    sourceTemplateId: null,
    outcomes: Object.freeze([]),
  }),
  reattempt: Object.freeze({
    mode: "disabled",
    maxReattempts: 0,
    thresholdPercentage: null,
    thresholdOutcomeId: null,
  }),
  finalResultPolicy: "highest",
});

let fallbackIdSequence = 0;

export const createOutcomeId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `outcome_${crypto.randomUUID()}`;
  }
  fallbackIdSequence += 1;
  return `outcome_${Date.now().toString(36)}_${fallbackIdSequence.toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

const clone = (value) => JSON.parse(JSON.stringify(value));

export const defaultAssessmentOutcomeConfig = () =>
  clone(DEFAULT_ASSESSMENT_OUTCOME_CONFIG);

const presetLabels = (type) => {
  switch (type) {
    case "passFail":
      return ["Pass", "Fail"];
    case "gcse9to1":
      return ["9", "8", "7", "6", "5", "4", "3", "2", "1", "U"];
    case "dmpf":
      return ["Distinction", "Merit", "Pass", "Fail"];
    case "custom":
      return ["Higher outcome", "Lowest outcome"];
    default:
      return [];
  }
};

export const createGradingPreset = (type, idFactory = createOutcomeId) => {
  if (!GRADING_TYPES.includes(type)) throw new Error("Unknown grading type.");
  if (type === "scoreOnly") {
    return { type, schemeName: null, sourceTemplateId: null, outcomes: [] };
  }
  const labels = presetLabels(type);
  return {
    type,
    schemeName: null,
    sourceTemplateId: null,
    outcomes: labels.map((label, index) => ({
      id: idFactory(),
      label,
      minPercentage: index === labels.length - 1 ? 0 : "",
    })),
  };
};

const cleanOptionalText = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return String(value).trim() || null;
};

export const validateGrading = (grading) => {
  const errors = [];
  if (!grading || typeof grading !== "object" || Array.isArray(grading)) {
    return { isValid: false, errors: ["Grading configuration is required."] };
  }
  if (!GRADING_TYPES.includes(grading.type)) errors.push("Select a valid grading type.");
  const outcomes = Array.isArray(grading.outcomes) ? grading.outcomes : [];

  if (grading.type === "scoreOnly") {
    if (outcomes.length !== 0) errors.push("Score Only cannot contain grade outcomes.");
    return { isValid: errors.length === 0, errors };
  }

  if (outcomes.length < 2) errors.push("Grading requires at least two outcomes.");
  if (outcomes.length > MAX_CUSTOM_OUTCOMES) {
    errors.push(`A grading scheme can contain at most ${MAX_CUSTOM_OUTCOMES} outcomes.`);
  }

  const ids = new Set();
  const labels = new Set();
  const thresholds = new Set();
  let previousThreshold = Infinity;
  outcomes.forEach((outcome, index) => {
    const id = typeof outcome?.id === "string" ? outcome.id.trim() : "";
    const label = typeof outcome?.label === "string" ? outcome.label.trim() : "";
    const labelKey = label.toLocaleLowerCase();
    const threshold = typeof outcome?.minPercentage === "number"
      ? outcome.minPercentage
      : Number(outcome?.minPercentage);

    if (!id) errors.push(`Outcome ${index + 1} needs a stable ID.`);
    else if (ids.has(id)) errors.push("Outcome IDs must be unique.");
    ids.add(id);

    if (!label) errors.push(`Outcome ${index + 1} needs a label.`);
    else if (labels.has(labelKey)) errors.push("Outcome labels must be unique.");
    labels.add(labelKey);

    if (!Number.isFinite(threshold) || outcome?.minPercentage === "") {
      errors.push(`${label || `Outcome ${index + 1}`} needs a valid minimum percentage.`);
      return;
    }
    if (threshold < 0 || threshold > 100) {
      errors.push(`${label || `Outcome ${index + 1}`} must be between 0% and 100%.`);
    }
    if (thresholds.has(threshold)) errors.push("Minimum percentages must be unique.");
    thresholds.add(threshold);
    if (index > 0 && threshold >= previousThreshold) {
      errors.push("Outcome thresholds must be strictly descending from highest to lowest.");
    }
    previousThreshold = threshold;
  });

  if (outcomes.length > 0 && Number(outcomes[outcomes.length - 1]?.minPercentage) !== 0) {
    errors.push("The lowest outcome must begin at 0%.");
  }

  return { isValid: errors.length === 0, errors: [...new Set(errors)] };
};

export const validateAssessmentOutcomeConfig = (config) => {
  const errors = [];
  if (!config || typeof config !== "object" || Array.isArray(config)) {
    return { isValid: false, errors: ["Assessment Outcomes configuration is required."] };
  }
  if (config.schemaVersion !== ASSESSMENT_OUTCOME_SCHEMA_VERSION) {
    errors.push("Unsupported Assessment Outcomes schema version.");
  }
  errors.push(...validateGrading(config.grading).errors);

  const reattempt = config.reattempt;
  if (!reattempt || typeof reattempt !== "object" || !REATTEMPT_MODES.includes(reattempt.mode)) {
    errors.push("Select a valid reattempt policy.");
  } else if (reattempt.mode === "disabled") {
    if (reattempt.maxReattempts !== 0) errors.push("Disabled reattempts must store a maximum of 0.");
    if (reattempt.thresholdPercentage !== null || reattempt.thresholdOutcomeId !== null) {
      errors.push("Disabled reattempts cannot contain a threshold.");
    }
  } else {
    const maximum = reattempt.maxReattempts === "" ? NaN : Number(reattempt.maxReattempts);
    if (!Number.isInteger(maximum) || maximum < 1 || maximum > 10) {
      errors.push("Maximum reattempts must be a whole number from 1 to 10.");
    }
    if (reattempt.mode === "belowPercentage") {
      const threshold = reattempt.thresholdPercentage === ""
        ? NaN
        : Number(reattempt.thresholdPercentage);
      if (!Number.isFinite(threshold) || threshold < 0 || threshold > 100) {
        errors.push("The reattempt percentage must be between 0% and 100%.");
      }
      if (reattempt.thresholdOutcomeId !== null) errors.push("Percentage reattempts cannot contain a grade threshold.");
    } else if (reattempt.mode === "belowGrade") {
      if (config.grading?.type === "scoreOnly") {
        errors.push("Grade-based reattempts require a grading scheme.");
      }
      const outcomes = Array.isArray(config.grading?.outcomes) ? config.grading.outcomes : [];
      const thresholdIndex = outcomes.findIndex(({ id }) => id === reattempt.thresholdOutcomeId);
      if (thresholdIndex < 0) errors.push("Select a valid grade threshold for reattempts.");
      else if (thresholdIndex === outcomes.length - 1) {
        errors.push("The lowest outcome cannot be used as a reattempt threshold.");
      }
      if (reattempt.thresholdPercentage !== null) errors.push("Grade reattempts cannot contain a percentage threshold.");
    } else if (reattempt.thresholdPercentage !== null || reattempt.thresholdOutcomeId !== null) {
      errors.push("Always-allowed reattempts cannot contain a threshold.");
    }
  }

  if (!FINAL_RESULT_POLICIES.includes(config.finalResultPolicy)) {
    errors.push("Select a valid final-result method.");
  }
  return { isValid: errors.length === 0, errors: [...new Set(errors)] };
};

export const buildAssessmentOutcomeConfig = (draft) => {
  const gradingType = draft?.grading?.type;
  const grading = gradingType === "scoreOnly"
    ? { type: "scoreOnly", schemeName: null, sourceTemplateId: null, outcomes: [] }
    : {
        type: gradingType,
        schemeName: cleanOptionalText(draft?.grading?.schemeName),
        sourceTemplateId: cleanOptionalText(draft?.grading?.sourceTemplateId),
        outcomes: (Array.isArray(draft?.grading?.outcomes) ? draft.grading.outcomes : []).map((outcome) => ({
          id: typeof outcome?.id === "string" ? outcome.id.trim() : "",
          label: typeof outcome?.label === "string" ? outcome.label.trim() : "",
          minPercentage: outcome?.minPercentage === "" ? "" : Number(outcome?.minPercentage),
        })),
      };
  const mode = draft?.reattempt?.mode;
  const reattempt = mode === "disabled"
    ? { mode: "disabled", maxReattempts: 0, thresholdPercentage: null, thresholdOutcomeId: null }
    : {
        mode,
        maxReattempts: Number(draft?.reattempt?.maxReattempts),
        thresholdPercentage: mode === "belowPercentage"
          ? draft?.reattempt?.thresholdPercentage === ""
            ? ""
            : Number(draft?.reattempt?.thresholdPercentage)
          : null,
        thresholdOutcomeId: mode === "belowGrade"
          ? cleanOptionalText(draft?.reattempt?.thresholdOutcomeId)
          : null,
      };
  const candidate = {
    schemaVersion: ASSESSMENT_OUTCOME_SCHEMA_VERSION,
    grading,
    reattempt,
    finalResultPolicy: draft?.finalResultPolicy ?? "highest",
  };
  const validation = validateAssessmentOutcomeConfig(candidate);
  if (!validation.isValid) {
    const error = new Error(validation.errors[0]);
    error.validationErrors = validation.errors;
    throw error;
  }
  return candidate;
};

export const normalizeAssessmentOutcomeConfig = (value) => {
  if (value === null || value === undefined) return defaultAssessmentOutcomeConfig();
  return buildAssessmentOutcomeConfig(value);
};

export const buildAssessmentOutcomeSnapshot = (quizConfig, capturedAt) => {
  if (capturedAt === null || capturedAt === undefined) {
    throw new Error("Assessment Outcomes snapshot capture time is required.");
  }
  const configSource = quizConfig === null || quizConfig === undefined
    ? "legacy-default"
    : "quiz";
  const normalized = normalizeAssessmentOutcomeConfig(quizConfig);
  return {
    schemaVersion: normalized.schemaVersion,
    grading: clone(normalized.grading),
    reattempt: clone(normalized.reattempt),
    finalResultPolicy: normalized.finalResultPolicy,
    configSource,
    capturedAt,
  };
};

export const copyTemplateGrading = (templateId, template) => {
  const grading = {
    ...clone(template?.grading || {}),
    schemeName: cleanOptionalText(template?.name) || cleanOptionalText(template?.grading?.schemeName),
    sourceTemplateId: templateId,
  };
  const validation = validateGrading(grading);
  if (!validation.isValid || grading.type === "scoreOnly") {
    throw new Error(validation.errors[0] || "Score Only cannot be applied as a reusable scheme.");
  }
  return clone(grading);
};

export const gradingForTemplate = (grading, name) => {
  const trimmedName = cleanOptionalText(name);
  if (!trimmedName) throw new Error("Enter a reusable scheme name.");
  if (trimmedName.length > 100) throw new Error("Reusable scheme names must be 100 characters or fewer.");
  const candidate = {
    type: grading?.type,
    schemeName: trimmedName,
    outcomes: clone(grading?.outcomes || []).map((outcome) => ({
      id: outcome.id,
      label: outcome.label,
      minPercentage: outcome.minPercentage,
    })),
  };
  const validation = validateGrading(candidate);
  if (!validation.isValid || candidate.type === "scoreOnly") {
    throw new Error(validation.errors[0] || "Score Only cannot be saved as a reusable scheme.");
  }
  return candidate;
};
